/**
 * permissions.ts - 权限检查逻辑
 *
 * 功能说明：
 * - 权限检查、角色检查逻辑
 * - 权限别名匹配规则
 *
 * 依赖关系：
 * - 依赖 store/auth/types.ts 的类型定义
 *
 * 关键注释索引：
 * - [权限检查] - 见下方 checkPermission 函数
 * - [别名匹配] - 见下方 edit/update 别名逻辑
 *
 * 创建日期：2026-05-03
 * 作者：AI Assistant
 */

import type { UserInfo } from '../types';

/**
 * [权限检查] 检查用户是否拥有指定权限
 *
 * @description 支持权限别名匹配：
 * - edit/update 互相匹配
 * - edit 继承 create
 *
 * @param user - 用户信息
 * @param permission - 权限字符串，格式 "resource:action"
 * @returns 是否拥有权限
 */
export const checkPermission = (user: UserInfo | null, permission: string): boolean => {
  if (!user) {
    return false;
  }

  if (!user.permissions || !Array.isArray(user.permissions)) {
    return false;
  }

  const permParts = permission.split(':');
  const resource = permParts[0];
  const action = permParts[1] || '';

  // [别名匹配] 支持 edit/update 别名匹配（语义等效：修改现有资源）
  if (action === 'edit' || action === 'update') {
    const aliasesToCheck = [permission, `${resource}:edit`, `${resource}:update`];
    if (aliasesToCheck.some(p => user.permissions.includes(p))) return true;
  }

  // [create继承] 支持 edit 继承 create（能编辑合同的人通常也能创建）
  if (action === 'create') {
    const aliasesToCheck = [permission, `${resource}:edit`, `${resource}:manage`];
    if (aliasesToCheck.some(p => user.permissions.includes(p))) return true;
  }

  return user.permissions.includes(permission);
};

/**
 * [角色检查] 检查用户是否拥有指定角色
 *
 * @param user - 用户信息
 * @param role - 角色名称
 * @returns 是否拥有角色
 */
export const hasRole = (user: UserInfo | null, role: string): boolean => {
  if (!user || !user.roles) {
    return false;
  }
  return user.roles.includes(role);
};

/**
 * [权限过期检查] 检查权限是否已过期
 *
 * @description 权限有效期为 1 小时
 *
 * @param user - 用户信息
 * @returns 是否已过期
 */
export const isPermissionsExpired = (user: UserInfo | null): boolean => {
  if (!user || !user.permissionsUpdatedAt) {
    return true; // 没有更新时间，视为过期
  }

  const ONE_HOUR = 60 * 60 * 1000;
  return Date.now() - user.permissionsUpdatedAt > ONE_HOUR;
};
